import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Download, Paperclip } from "lucide-react";
import { toast } from "sonner";

import api, { API } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Loading } from "../../components/shared";

export default function ResidentNoticeDetail() {
  const { noticeId } = useParams();
  const [data, setData] = useState(null);

  const load = useCallback(() => {
    api
      .get(`/resident-communications/notices/${noticeId}`)
      .then((response) => {
        setData(response.data);
        api
          .post(`/resident-communications/notices/${noticeId}/read`)
          .catch(() => {});
      })
      .catch(() => setData({ missing: true }));
  }, [noticeId]);

  useEffect(() => {
    load();
  }, [load]);

  const acknowledge = async () => {
    try {
      await api.post(
        `/resident-communications/notices/${noticeId}/acknowledge`,
      );
      toast.success("Acknowledgement recorded");
      load();
    } catch (error) {
      toast.error(
        error.response?.data?.detail || "Could not record acknowledgement",
      );
    }
  };

  if (!data) return <Loading />;
  if (data.missing) {
    return (
      <p
        className="border border-dashed border-slate-300 bg-white p-6 text-sm text-slate-600"
        data-testid="resident-notice-missing"
      >
        This notice is not available.
      </p>
    );
  }

  const { notice, society, attachments } = data;
  return (
    <article data-testid="resident-notice-detail-page">
      <Link
        className="text-sm font-semibold text-blue-700 hover:text-blue-900"
        data-testid="resident-notice-back"
        to="/resident/notices"
      >
        ← All notices
      </Link>
      <header className="mt-5 border-b border-slate-200 pb-6">
        <div className="flex flex-wrap items-center gap-3">
          {society.logo_data_url && (
            <img
              alt=""
              className="h-10 w-10 object-contain"
              src={society.logo_data_url}
            />
          )}
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
            {society.name}
          </p>
        </div>
        <p className="mt-5 text-xs font-semibold text-blue-700">
          {notice.type} · {notice.category}
        </p>
        <h1 className="mt-2 font-display text-4xl font-bold text-slate-900">
          {notice.title}
        </h1>
        {notice.subject && (
          <p className="mt-3 text-lg text-slate-600">{notice.subject}</p>
        )}
        <p className="mt-4 text-xs text-slate-500">
          Published {(notice.publish_at || "").slice(0, 10)} ·{" "}
          {notice.published_by_designation}
        </p>
      </header>
      <div
        className="mt-7 whitespace-pre-wrap text-sm leading-7 text-slate-800"
        data-testid="resident-notice-body"
      >
        {notice.body}
      </div>
      {notice.acknowledgement_required && (
        <section
          className="mt-8 border border-amber-200 bg-amber-50 p-5"
          data-testid="resident-notice-acknowledgement"
        >
          <p className="text-sm font-semibold text-amber-900">
            Acknowledgement required
          </p>
          <p className="mt-1 text-sm text-amber-800">
            This records that you received this communication.
          </p>
          <Button
            className="mt-4"
            data-testid="resident-notice-acknowledge"
            disabled={data.acknowledged}
            onClick={acknowledge}
          >
            {data.acknowledged ? "Acknowledged" : "Acknowledge"}
          </Button>
        </section>
      )}
      {attachments.length > 0 && (
        <section
          className="mt-8 border-t border-slate-200 pt-6"
          data-testid="resident-notice-attachments"
        >
          <h2 className="font-display text-xl font-semibold">Attachments</h2>
          <div className="mt-3 space-y-2">
            {attachments.map((attachment) => (
              <div
                className="flex flex-wrap items-center justify-between gap-3 border border-slate-200 bg-white p-3"
                key={attachment.doc_id}
              >
                <span className="flex items-center gap-2 text-sm">
                  <Paperclip className="h-4 w-4" />
                  {attachment.title}
                </span>
                <a
                  className="inline-flex items-center gap-2 text-sm font-semibold text-blue-700 hover:text-blue-900"
                  data-testid={`resident-notice-attachment-download-${attachment.doc_id}`}
                  href={`${API}/resident-communications/notices/${noticeId}/attachments/${attachment.doc_id}/download`}
                  rel="noreferrer"
                  target="_blank"
                >
                  <Download className="h-4 w-4" />
                  Download
                </a>
              </div>
            ))}
          </div>
        </section>
      )}
    </article>
  );
}
